import { memo, useEffect, useMemo, useRef, useState } from 'react';

import Chip from '../../../Chip';
import { Dropdown, Flex, Input, Space } from 'antd';
import { CloseIcon2, GridIcon, OrderCompactIcon, OrderListIcon, SearchIcon } from '../../../Icons';
import type { InputRef } from 'antd';

// Utils
import { useTranslation } from 'react-i18next';

// Redux
import { useAppDispatch, useAppSelector } from '../../../../store/store';
import {
  normalizeLibraryFilter,
  yourLibraryActions,
  YourLibraryState,
} from '../../../../store/slices/yourLibrary';

const VIEW = ['COMPACT', 'LIST', 'GRID'] as const;

const SearchSelector = memo(() => {
  const dispatch = useAppDispatch();
  const [t] = useTranslation('navbar');
  const search = useAppSelector((state) => state.yourLibrary.search);
  const [isOpen, setIsOpen] = useState(() => Boolean(search));
  const inputRef = useRef<InputRef | null>(null);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus({ cursor: 'end' });
    }
  }, [isOpen]);

  useEffect(() => {
    if (search && !isOpen) {
      setIsOpen(true);
    }
  }, [search, isOpen]);

  const closeSearch = () => {
    setIsOpen(false);
    dispatch(yourLibraryActions.setSearch({ search: '' }));
  };

  if (!isOpen) {
    return (
      <button
        className='addButton'
        aria-label={t('Search')}
        onClick={() => {
          setIsOpen(true);
        }}
      >
        <SearchIcon style={{ height: '1rem' }} />
      </button>
    );
  }

  return (
    <Input
      className='library-search-input'
      value={search}
      allowClear
      size='middle'
      placeholder={t('Search')}
      prefix={<SearchIcon style={{ height: '1rem' }} />}
      onChange={(event) => {
        dispatch(yourLibraryActions.setSearch({ search: event.target.value }));
      }}
      style={{ flex: 1, minWidth: 0 }}
      ref={inputRef}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          closeSearch();
        }
      }}
      suffix={
        <button
          type='button'
          aria-label={t('Close search')}
          onClick={() => {
            closeSearch();
          }}
          style={{
            background: 'transparent',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'inherit',
          }}
        >
          <CloseIcon2 />
        </button>
      }
    />
  );
});

const ViewSelector = memo(() => {
  const dispatch = useAppDispatch();
  const [t] = useTranslation('navbar');
  const view = useAppSelector((state) => state.yourLibrary.view);

  const items = VIEW.map((view) => ({
    key: view,
    label: t(view),
    onClick: () => {
      dispatch(yourLibraryActions.setView({ view }));
    },
  }));

  return (
    <Dropdown
      placement='bottomRight'
      className='viewSelector'
      menu={{ items, selectedKeys: [view] }}
      trigger={['click']}
    >
      <button className='order-button'>
        <Space align='center'>
          <span>{t(view)}</span>
          {view === 'GRID' ? <GridIcon style={{ height: '1rem' }} /> : null}
          {view === 'LIST' ? <OrderListIcon style={{ height: '1rem' }} /> : null}
          {view === 'COMPACT' ? <OrderCompactIcon style={{ height: '1rem' }} /> : null}
        </Space>
      </button>
    </Dropdown>
  );
});

const SortSelector = memo(() => {
  const dispatch = useAppDispatch();
  const [t] = useTranslation('navbar');
  const filter = useAppSelector((state) => state.yourLibrary.filter);
  const composerSortOrder = useAppSelector((state) => state.yourLibrary.composerSortOrder);
  const hasComposers = useAppSelector((state) => state.yourLibrary.myComposers.length > 0);

  const normalizedFilter = useMemo(() => normalizeLibraryFilter(filter), [filter]);
  const isComposerView = normalizedFilter === 'COMPOSERS';

  if (!isComposerView || !hasComposers) {
    return null;
  }

  const items = [
    {
      key: 'default',
      label: t('LibrarySortDefault'),
      onClick: () => {
        dispatch(yourLibraryActions.setComposerSortOrder({ composerSortOrder: 'default' }));
      },
    },
    {
      key: 'relevance',
      label: t('LibrarySortRelevance'),
      onClick: () => {
        dispatch(yourLibraryActions.setComposerSortOrder({ composerSortOrder: 'relevance' }));
      },
    },
  ];

  const activeLabel =
    composerSortOrder === 'relevance' ? t('LibrarySortRelevance') : t('LibrarySortDefault');

  return (
    <Dropdown
      placement='bottomRight'
      className='viewSelector'
      menu={{ items, selectedKeys: [composerSortOrder] }}
      trigger={['click']}
    >
      <button className='order-button'>
        <Space align='center'>
          <span>{activeLabel}</span>
          <OrderListIcon style={{ height: '1rem' }} />
        </Space>
      </button>
    </Dropdown>
  );
});

export const SearchArea = () => {
  return (
    <Flex
      align='center'
      justify='space-between'
      gap={8}
      style={{ margin: '0px 10px', marginBottom: 10 }}
    >
      <SearchSelector />
      <SortSelector />
      <ViewSelector />
    </Flex>
  );
};

const TypeSelector = memo(() => {
  const dispatch = useAppDispatch();
  const [t] = useTranslation('navbar');
  const filter = useAppSelector((state) => state.yourLibrary.filter);

  const onClick = (filter: YourLibraryState['filter']) => {
    dispatch(yourLibraryActions.setFilter({ filter }));
  };

  const items = useMemo(() => {
    return [
      { text: 'Playlists', type: 'PLAYLISTS' as const },
      { text: 'Artists', type: 'ARTISTS' as const },
      { text: 'Albums', type: 'ALBUMS' as const },
      { text: 'Composers', type: 'COMPOSERS' as const },
      { text: 'Works', type: 'WORKS' as const },
      { text: 'Recordings', type: 'RECORDINGS' as const },
    ];
  }, []);

  return (
    <Space wrap>
      {filter !== 'ALL' ? (
        <Chip key='close' text={<CloseIcon2 />} onClick={() => onClick('ALL')} />
      ) : null}

      {items.map(({ text, type }) => {
        if (filter === 'ALL' || type === filter) {
          return (
            <Chip
              key={text}
              text={t(text)}
              active={filter === type}
              onClick={() => onClick(type)}
            />
          );
        }
        return null;
      })}
    </Space>
  );
});

export const LibraryFilters = () => {
  return (
    <div>
      <div>
        <TypeSelector />
      </div>
    </div>
  );
};
